import { BaseSyntheticEvent, useCallback, useMemo } from 'react';
import { FieldErrors, UseFormRegister, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { useLoggedInUserRoles } from '~/components/Auth';
import { AdminWorkspaceRoles } from '~/data/user/role';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { yupValidateWorkspaceName } from '~/scenes/MyWorkspace/components/WorkspaceToolbar/components/WorkspaceToolbarReadMode/components/WorkspaceSetupDialog';
import { WorkspaceType } from '~/scenes/MyWorkspace/data';
import {
  useUpdateWorkspaceSettings,
  useWorkspaceSettings,
} from '~/scenes/MyWorkspace/hooks';
import { isRoleHRAdmin } from '~/services/Permissions';

import { leaveManageWorkspace } from '../../../../layoutSlice';
import { myWorkspaceLayoutStoreStateSelector } from '../../../../selectors';
import { WorkspaceSettings } from '../../../../userSettings';

export interface WorkspaceEditFormValues {
  name: string;
  restrictToRoles: string[];
}

export interface UseWorkspaceEditOutput {
  isEditing: boolean;
  register: UseFormRegister<WorkspaceEditFormValues>;
  errors: FieldErrors<WorkspaceEditFormValues>;
  defaultName: string;
  defaultType: WorkspaceType | string;
  handleSave: (e?: BaseSyntheticEvent) => Promise<void>;
  handleCancel: () => void;
  activeWorkspace: WorkspaceSettings;
  isHRAdmin: boolean;
}

export const useWorkspaceToolbarEditMode = (): UseWorkspaceEditOutput => {
  const { workspaces, activeWorkspace } = useWorkspaceSettings();
  const { manageWorkspace } = useSelector(myWorkspaceLayoutStoreStateSelector);

  const manageWorkspaceId = manageWorkspace?.id;
  const isEditing = manageWorkspaceId === activeWorkspace.id;
  const defaultName = manageWorkspace?.name || '';
  const defaultType = manageWorkspace?.type || WorkspaceType.Default;
  const defaultRestrictToRoles = manageWorkspace?.restrictToRoles || [];

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const roles = useLoggedInUserRoles();
  const isHRAdmin = isRoleHRAdmin(roles);

  const validationSchema = useMemo(() => {
    return yup.object().shape<WorkspaceEditFormValues>({
      name: yupValidateWorkspaceName(workspaces, manageWorkspaceId, t),
      // TODO: Validate restrictToRoles against Roles Enum
      restrictToRoles: yup.array().of(yup.string().required()).required(),
      // restrictToRoles: yup
      //   .array()
      //   .of(yup.mixed<Roles>().oneOf(Object.values(Roles)).required())
      //   .required(),
    });
  }, [t, workspaces, manageWorkspaceId]);

  const resolver = useYupValidationResolver(validationSchema);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<WorkspaceEditFormValues>({
    defaultValues: {
      name: defaultName,
      restrictToRoles: defaultRestrictToRoles,
    },
    resolver,
  });

  const { editWorkspacesSettings } = useUpdateWorkspaceSettings();

  const handleSave = useMemo(() => {
    return handleSubmit((values: WorkspaceEditFormValues) => {
      if (manageWorkspace) {
        // New workspace settings
        const workspaceChangesForType = {
          workspaces: isEditing
            ? workspaces.map((workspace): WorkspaceSettings => {
                if (workspace.id === manageWorkspace.id) {
                  return {
                    ...manageWorkspace,
                    name: values.name,
                    restrictToRoles:
                      values.restrictToRoles as AdminWorkspaceRoles[],
                  };
                }

                return workspace;
              })
            : [...workspaces, { ...manageWorkspace, name: values.name }],
        };

        // New active workspace
        const activeWorkspaceChange = {
          activeWorkspaceId: manageWorkspace.id,
        };

        editWorkspacesSettings({
          ...workspaceChangesForType,
          ...activeWorkspaceChange,
        });
      }

      dispatch(leaveManageWorkspace());
    });
  }, [
    editWorkspacesSettings,
    handleSubmit,
    manageWorkspace,
    isEditing,
    workspaces,
    dispatch,
  ]);

  const handleCancel = useCallback(() => {
    dispatch(leaveManageWorkspace());
  }, [dispatch]);

  return {
    isEditing,
    register,
    errors,
    defaultName,
    defaultType,
    handleSave,
    handleCancel,
    activeWorkspace: manageWorkspace || activeWorkspace,
    isHRAdmin,
  };
};
